import { useMemo } from "react";
import { useGlobalData } from "./useGlobalHooks";

/**
 * Calculate total multiplier for a user
 */
function calculateTotalMultiplier(userMapping) {
  return (
    (userMapping.index_multiplier || 0) +
    (userMapping.banknifty_multiplier || 0) +
    (userMapping.finnifty_multiplier || 0) +
    (userMapping.midcpnifty_multiplier || 0) +
    (userMapping.sensex_multiplier || 0) +
    (userMapping.stock_multiplier || 0)
  );
}

/**
 * Calculate total size (quantity) for a client across all positions
 */
function calculateTotalSize(positions, clientId) {
  if (!Array.isArray(positions)) return 0;
  return positions
    .filter((p) => p.client_id === clientId)
    .reduce((sum, p) => sum + (p.qty || 0), 0);
}

/**
 * Calculate open value for a client
 * Open Value = Sum of (avg_price * qty) for all positions
 */
function calculateOpenValue(positions, clientId) {
  if (!Array.isArray(positions)) return 0;
  return positions
    .filter((p) => p.client_id === clientId)
    .reduce((sum, p) => sum + (p.avg_price || 0) * (p.qty || 0), 0);
}

/**
 * Calculate cash available
 * Cash Available = Balance - Open Value
 */
function calculateCashAvailable(balance, openValue) {
  return balance - openValue;
}

/**
 * Hook for Account View data
 * Combines data from multiple sources to create account overview
 *
 * @returns {Object} { data, loading, error }
 */
export function useAccountView() {
  const {
    positions,
    userMappings,
    users,
    indexMultipliers,
    indexMultipliersRaw,
    loading,
    multiAssetUnderlyings,
  } = useGlobalData();

  const accountViewData = useMemo(() => {
    // Only wait for essential data (not positions for initial render)
    if (loading.userMappings || loading.users || loading.indexMultipliers) {

      return [];
    }
    // console.log("all Users Here---mmmm-----",users);
    // Validate data exists and is array
    if (!Array.isArray(userMappings)) {
      console.log("userMappings is not an array:", userMappings);
      return [];
    }

    if (!Array.isArray(users)) {
      console.log("users is not an array:", users);
      return [];
    }

    // Positions can be empty initially (will update when available)
    const positionsArray = Array.isArray(positions) ? positions : [];

    // console.log("📊 Building account view:", {
    //   userMappings: userMappings.length,
    //   users: users.length,
    //   positions: positionsArray.length,
    //   positionsLoaded: !loading.positions,
    //   ordersLoaded: !loading.orders,
    //   timestamp: new Date().toISOString(),
    // });

    // console.log("🚀 ACCOUNT VIEW DATA READY - Should render NOW!");
    performance.mark("account-view-data-ready");

    // Create a combined list of all users from both userMappings and users arrays
    // Use composite key (client_id + broker) to handle same client_id with different brokers
    const allUsers = [];
    const seenKeys = new Set();

    // Add from userMappings (keep original broker names)
    userMappings.forEach((um) => {
      const key = `${um.client_id}_${um.broker || 'unknown'}`;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        allUsers.push({ client_id: um.client_id, broker: um.broker });
      }
    });

    // Add from users (keep original broker names)
    users.forEach((u) => {
      const key = `${u.client_id}_${u.broker || 'unknown'}`;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        allUsers.push({ client_id: u.client_id, broker: u.broker });
      }
    });

    // Build account view data for all users
    const data = allUsers
      .map(({ client_id: clientId, broker }) => {
        // Find corresponding entries with flexible broker matching
        // Try exact match first, then try normalized versions
        let userMapping = userMappings.find(
          (um) => um.client_id === clientId && um.broker === broker
        );

        // If not found and broker is dhan_anurag, try dhan_ss_anurag
        if (!userMapping && broker === 'dhan_anurag') {
          userMapping = userMappings.find(
            (um) => um.client_id === clientId && um.broker === 'dhan_ss_anurag'
          );
        }

        // If not found and broker is dhan_ss_anurag, try dhan_anurag
        if (!userMapping && broker === 'dhan_ss_anurag') {
          userMapping = userMappings.find(
            (um) => um.client_id === clientId && um.broker === 'dhan_anurag'
          );
        }


        const user = users.find((u) => u.client_id === clientId && u.broker === broker);

        // Log user data for debugging
        // if (userMapping.client_id) {
        //   console.log(`👤 User ${userMapping.client_id}:`, {
        //     name: user?.name,
        //     cash_input: user?.cash_input,
        //     discount: user?.discount,
        //     full_user: user,
        //   });
        // }

        // Find index multiplier data (safely handle if not array)
        // Find index multiplier data (raw data contains mcx_multiplier and other fields)
        // Use flexible broker matching
        let indexMul = Array.isArray(indexMultipliers)
          ? indexMultipliers.find((im) => im.client_id === clientId && im.broker === broker)
          : null;

        if (!indexMul && broker === 'dhan_anurag') {
          indexMul = Array.isArray(indexMultipliers)
            ? indexMultipliers.find((im) => im.client_id === clientId && im.broker === 'dhan_ss_anurag')
            : null;
        }

        if (!indexMul && broker === 'dhan_ss_anurag') {
          indexMul = Array.isArray(indexMultipliers)
            ? indexMultipliers.find((im) => im.client_id === clientId && im.broker === 'dhan_anurag')
            : null;
        }

        let indexMulRaw = Array.isArray(indexMultipliersRaw)
          ? indexMultipliersRaw.find((im) => im.client_id === clientId && im.broker === broker)
          : null;

        if (!indexMulRaw && broker === 'dhan_anurag') {
          indexMulRaw = Array.isArray(indexMultipliersRaw)
            ? indexMultipliersRaw.find((im) => im.client_id === clientId && im.broker === 'dhan_ss_anurag')
            : null;
        }

        if (!indexMulRaw && broker === 'dhan_ss_anurag') {
          indexMulRaw = Array.isArray(indexMultipliersRaw)
            ? indexMultipliersRaw.find((im) => im.client_id === clientId && im.broker === 'dhan_anurag')
            : null;
        }

        // Get balance
        const balance = user?.balance || 0;

        // Calculate multipliers - check product column first, then fallback to multi_asset_multiplier
        const productVal = (user?.product || userMapping?.product || "").toLowerCase();
        const multiAssetMul = Number(
          userMapping?.multi_asset_multiplier ??
          userMapping?.multiasset_multiplier ??
          indexMulRaw?.multi_asset_multiplier ??
          indexMulRaw?.multiasset_multiplier ??
          0
        );
        const isMultiAsset = productVal === 'multi_asset' || productVal === 'multiasset';
        const validMaUnderlyings = (multiAssetUnderlyings || ['NIFTY', 'BANKNIFTY']).map((u) => u.toUpperCase());

        const rawNiftyMul =
          userMapping?.index_multiplier ||
          userMapping?.multiplier ||
          indexMulRaw?.multiplier ||
          user?.multiplier ||  // fallback: raw user record (covers Alpaca and non-index brokers)
          0;
        const rawBnfMul =
          userMapping?.banknifty_multiplier ||
          indexMulRaw?.banknifty_multiplier ||
          0;
        const rawFinniftyMul =
          userMapping?.finnifty_multiplier ||
          indexMulRaw?.finnifty_multiplier ||
          0;
        const rawMidcpniftyMul =
          userMapping?.midcpnifty_multiplier ||
          indexMulRaw?.midcpnifty_multiplier ||
          0;
        const rawSensexMul =
          userMapping?.sensex_multiplier || indexMulRaw?.sensex_multiplier || 0;
        const stockMul =
          userMapping?.stock_multiplier || indexMulRaw?.stock_multiplier || 0;
        // MCX multiplier: prefer explicit field on userMapping, then raw index multipliers API, then user record
        const mcxMul =
          userMapping?.mcx_multiplier ||
          (indexMulRaw && indexMulRaw.mcx_multiplier) ||
          user?.mcx_multiplier ||
          0;

        // When multi_asset_multiplier is active (> 0), it ONLY applies to underlyings in multi_asset_regime_grid (e.g. NIFTY, BANKNIFTY)
        // For any non-grid index (e.g. FINNIFTY, MIDCPNIFTY, SENSEX), multi-asset users have multiplier 0.
        const effectiveMaMul = multiAssetMul > 0 ? multiAssetMul : (rawNiftyMul || 1);
        const niftyMul = isMultiAsset ? (validMaUnderlyings.includes('NIFTY') ? effectiveMaMul : 0) : rawNiftyMul;
        const bnfMul = isMultiAsset ? (validMaUnderlyings.includes('BANKNIFTY') ? effectiveMaMul : 0) : rawBnfMul;
        const finniftyMul = isMultiAsset ? (validMaUnderlyings.includes('FINNIFTY') ? effectiveMaMul : 0) : rawFinniftyMul;
        const midcpniftyMul = isMultiAsset ? (validMaUnderlyings.includes('MIDCPNIFTY') ? effectiveMaMul : 0) : rawMidcpniftyMul;
        const sensexMul = isMultiAsset ? (validMaUnderlyings.includes('SENSEX') ? effectiveMaMul : 0) : rawSensexMul;

        const totalMul =
          niftyMul +
          bnfMul +
          finniftyMul +
          midcpniftyMul +
          sensexMul +
          stockMul +
          mcxMul;

        // Calculate totals (use positionsArray instead of positions)
        // Filter positions by BOTH client_id AND broker
        const totalSize = positionsArray
          .filter((p) => p.client_id === clientId && p.broker === broker)
          .reduce((sum, p) => sum + (p.qty || 0), 0);
        const openValue = positionsArray
          .filter((p) => p.client_id === clientId && p.broker === broker)
          .reduce((sum, p) => sum + (p.avg_price || 0) * (p.qty || 0), 0);
        const cashAvailable = calculateCashAvailable(balance, openValue);

        // Get cash_input and discount from user data (from API)
        const cashInput = user?.cash_input || 0;
        const discountInput = user?.discount || 0;
        const commentInput = user?.comment || "";
        const flag = user?.label || "-";
        const tokenGenerated = user?.is_token_generated ? "Yes" : "No";
        const label = user?.label || "";

        // Determine if user has any active multipliers
        const hasMultiplier =
          rawNiftyMul > 0 ||
          rawBnfMul > 0 ||
          rawFinniftyMul > 0 ||
          rawMidcpniftyMul > 0 ||
          rawSensexMul > 0 ||
          stockMul > 0 ||
          mcxMul > 0 ||
          multiAssetMul > 0;

        // Determine if user has any activity (positions or multipliers)
        const hasActivity = totalSize > 0 || openValue > 0 || hasMultiplier;

        // Exclude dummy users with NO activity (no positions AND all multipliers = 0)
        if (user?.status === "dummy" && !hasActivity) {
          return null;
        }

        // Skip if we have neither user nor userMapping data
        if (!user && !userMapping) {
          return null;
        }

        return {
          name: userMapping?.name || user?.name || user?.client_id || "Unknown",
          clientId: clientId,
          broker: broker || user?.broker || userMapping?.broker || "-",
          status: userMapping?.status || user?.status || "unknown",
          product: productVal === 'multi_asset' || productVal === 'multiasset' ? 'multi_asset' : 'optimus',
          cashInput,
          multiAssetMul,
          multi_asset_multiplier: multiAssetMul,
          isMultiAsset,
          rawNiftyMul,
          rawBnfMul,
          rawFinniftyMul,
          rawMidcpniftyMul,
          rawSensexMul,
          niftyMul,
          bnfMul,
          finniftyMul,
          midcpniftyMul,
          sensexMul,
          stockMul,
          mcxMul,
          totalMul,
          cashAvailable,
          openValue,
          totalSize,
          discountInput,
          commentInput,
          flag,
          tokenGenerated,
          label,
          proxyToggle: !!user?.proxy_toggle,
          is_pledge_plus: user?.is_pledge_plus,
          is_pledgeplus_mini: user?.is_pledgeplus_mini,
          balance_updated_at: user?.balance_updated_at || null
        };
      })
      .filter(Boolean); // Remove null entries

    // console.log("📊 Account View processed data:", {
    //   totalUsers: data.length,
    //   userMappingsCount: userMappings.length,
    //   usersCount: users.length,
    //   brokerBreakdown: data.reduce((acc, user) => {
    //     const broker = user.broker || "unknown";
    //     acc[broker] = (acc[broker] || 0) + 1;
    //     return acc;
    //   }, {}),
    // });

    // Sort data: Active users first (alphabetically), then inactive users (alphabetically)
    data.sort((a, b) => {
      // First sort by status (active first)
      if (a.status === "active" && b.status !== "active") return -1;
      if (a.status !== "active" && b.status === "active") return 1;

      // Then sort alphabetically by name (handle potential missing names)
      const aName = a.name || a.clientId || "";
      const bName = b.name || b.clientId || "";
      return aName.localeCompare(bName);
    });

    return data;
  }, [
    positions,
    userMappings,
    users,
    indexMultipliers,
    indexMultipliersRaw,
    loading.userMappings,
    loading.users,
    loading.indexMultipliers,
    multiAssetUnderlyings,
  ]);

  const isLoading =
    loading.userMappings || loading.users || loading.indexMultipliers;

  return {
    data: accountViewData,
    loading: isLoading,
  };
}
